import type { UserProfileDto } from "@/types/user";

import useSWR, { mutate } from "swr";
import { CONFIG } from "@/config-global";
import axios, { fetcher, endpoints } from "@/util/axios";

// ----------------------------------------------------------------------
const USER_ENDPOINT = `${CONFIG.serverUrl}${endpoints.user}`;

const ENDPOINTS = {
  getUserProfile: "get-user-profile",
  updateUserProfile: "update-user-profile",
  deleteUserProfile: "delete-user-profile",
  registerUser: "register-user",
};

const swrOptions = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

// ----------------------------------------------------------------------

export function useGetUserProfile(userId: string) {
  const url = userId
    ? [
        `${USER_ENDPOINT}`,
        { params: { id: userId, endpoint: ENDPOINTS.getUserProfile } },
      ]
    : null;

  const { data, error, isLoading, isValidating } = useSWR<{
    user: UserProfileDto;
  }>(url, fetcher, swrOptions);

  return {
    user: data?.user,
    userLoading: isLoading,
    userError: error,
    userValidating: isValidating,
    userRefresh: mutate,
  };
}

// ----------------------------------------------------------------------

export async function deleteUserProfile(userId: string) {
  const params = { id: userId, endpoint: ENDPOINTS.deleteUserProfile };

  const response = await axios.delete(USER_ENDPOINT, { params });
  return response.data;
}

// ----------------------------------------------------------------------

export async function registerUser(userData: {
  email: string;
  id: string;
  firstName: string;
  lastName: string;
  profilePictureUrl?: string;
}) {
  const params = { endpoint: ENDPOINTS.registerUser };
  const url = `${CONFIG.serverUrl}${endpoints.register}`;

  const response = await axios.post(
    url,
    { registerData: userData },
    { params }
  );
  return response.data;
}

// ----------------------------------------------------------------------

export async function updateUserOnboarding(
  userId: string,
  onboardingData: {
    jobTitle: string;
    organizationName: string;
    organizationType: string;
    organizationSize: string;
    formTypes: string[];
    // complianceNeeds: string[];
  }
) {
  const params = { id: userId, endpoint: ENDPOINTS.updateUserProfile };

  const response = await axios.patch(
    USER_ENDPOINT,
    {
      onboardingCompleted: true,
      ...onboardingData,
    },
    { params }
  );

  // Invalidate the user profile cache to refresh data
  mutate([
    `${USER_ENDPOINT}`,
    { params: { id: userId, endpoint: ENDPOINTS.getUserProfile } },
  ]);

  return response.data;
}

// ----------------------------------------------------------------------

export async function updateUserProfile(
  userId: string,
  profileData: {
    firstName?: string;
    lastName?: string;
    [key: string]: any;
  }
) {
  const params = { id: userId, endpoint: ENDPOINTS.updateUserProfile };

  const response = await axios.patch(USER_ENDPOINT, profileData, { params });

  // Invalidate the user profile cache to refresh data
  mutate([
    `${USER_ENDPOINT}`,
    { params: { id: userId, endpoint: ENDPOINTS.getUserProfile } },
  ]);

  return response.data;
}

// ----------------------------------------------------------------------

export async function updatePreferences(
  userId: string,
  preferencesData: {
    emailNotifications?: boolean;
    marketingEmails?: boolean;
    [key: string]: any;
  }
) {
  const params = { id: userId, endpoint: ENDPOINTS.updateUserProfile };

  const response = await axios.patch(USER_ENDPOINT, preferencesData, {
    params,
  });

  // Invalidate the user profile cache to refresh data
  mutate([
    `${USER_ENDPOINT}`,
    { params: { id: userId, endpoint: ENDPOINTS.getUserProfile } },
  ]);

  return response.data;
}
